import React, { useState,useCallback,useMemo } from 'react';
import { TruckIcon } from 'lucide-react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Base_Url, priceEstimatorPublic, getpincodePublic } from '../../config/config';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useEffect } from 'react';
// Define Zod schema for form validation
import ShippingEstimateModal from '../b2c/models/estimateModelSignup';

const formSchema = z.object({
    pickupPincode: z.string().min(1, 'Pickup pincode is required').length(6, 'Pincode must be 6 digits'),
    dropPincode: z.string().min(1, 'Drop pincode is required').length(6, 'Pincode must be 6 digits'),
    length: z.string().min(1, 'Length is required').transform(val => Number(val)),
    width: z.string().min(1, 'Width is required').transform(val => Number(val)),
    height: z.string().min(1, 'Height is required').transform(val => Number(val)),
    weight: z.string().min(1, 'Weight is required').transform(val => Number(val)),
    weightUnit: z.enum(['gram', 'kg']),
    // shipmentType: z.string().min(1, 'Shipment type is required'),
    // declaredValue: z.string().optional(),
    prepaid: z.string().min(1, 'Please select prepaid or COD'),
    codAmount: z.string().optional()
        .refine(val => val === undefined || val === '' || Number(val) > 0, {
            message: 'COD amount must be greater than 0',
            path: ['codAmount']
        })
});

const ShippingRateCalculator = () => {
    const [pickupCity, setPickupCity] = useState('');
    const [dropCity, setDropCity] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [prepaidValue, setPrepaidValue] = useState('');
    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Initialize form with React Hook Form and Zod resolver
    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        clearErrors,
        formState: { errors }
    } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            weightUnit: 'gram',
            prepaid: '',
        }
    });

    const handlePrepaidChange = (e) => {
        setPrepaidValue(e.target.value);
    };

// Destructure constants for token and cus_id once for reusability
const token = localStorage.getItem("token") || "eyJ1c2VyX2lkIjoiNyIsImV4cGlyZXNfYXQiOjE3Mjc1MTc2Nzd9";
const cus_id = localStorage.getItem("cus_id") || "7";

const headers = useMemo(() => ({
  token,
  cus_id,
  "Content-Type": "application/json",
}), [token, cus_id]);

// Watch pincode changes
const pickupPincode = watch('pickupPincode');
const dropPincode = watch('dropPincode');

// Clear city function
const clearCity = useCallback((type) => {
  if (type === 'pickup') {
    setPickupCity('');
  } else {
    setDropCity('');
  }
}, [setPickupCity, setDropCity]);

// Fetch pincode data
const fetchPincodeData = useCallback(async (pincode, type) => {
  const url = `${Base_Url}${getpincodePublic}?pincode=${pincode}`;
  try {
    const response = await fetch(url, { method: 'GET', headers });
    const data = await response.json();

    if (data?.status === "SUCCESS") {
      let { city } = data.data[0];
      city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
      if (type === 'pickup') {
        setPickupCity(city);
      } else {
        setDropCity(city);
      }
    } else {
      clearCity(type);  
      toast.error("Failed to fetch city data");
    }
  } catch (error) {
    console.error('Error fetching pincode data:', error);
    toast.error("Error fetching pincode data");
  }
}, [headers, clearCity]);

// Handle pincode input change
const handlePincodeChange = useCallback((e, type) => {
  const { value } = e.target;
  const filteredValue = value.replace(/[^0-9]/g, '');
  const field = type === 'pickup' ? 'pickupPincode' : 'dropPincode';

  setValue(field, filteredValue);
  clearCity(type);

  if (filteredValue.length === 6) {
    clearErrors(field);
    fetchPincodeData(filteredValue, type);
  }
}, [setValue, clearErrors, fetchPincodeData, clearCity]);

// Fetch city when pincode changes - debounce like effect
useEffect(() => {
  if (pickupPincode?.length === 6) {
    fetchPincodeData(pickupPincode, 'pickup');
  } else {
    clearCity('pickup');
  }
}, [pickupPincode, fetchPincodeData, clearCity]);

useEffect(() => {
  if (dropPincode?.length === 6) {
    fetchPincodeData(dropPincode, 'drop');
  } else {
    clearCity('drop');
  }
}, [dropPincode, fetchPincodeData, clearCity]);

// Submit form
const onSubmit = async (data) => {
    setIsProcessing(true);
  
    // Determine weight in kg based on selected unit
    // let weightInKg = parseFloat(data.weight);
    // if (data.weightUnit === 'gram') {
    //   weightInKg = weightInKg / 1000;
    // }
  
    const requestBody = {
      from_pincode: data.pickupPincode,
      to_pincode: data.dropPincode,
      length: parseFloat(data.length),
      width: parseFloat(data.width),
      height: parseFloat(data.height),
      weight: parseFloat(data.weight)/1000, // Use the correctly calculated weight
      shipment_type: data.shipmentType,
      declared_value: data.declaredValue ? parseFloat(data.declaredValue) : "",
      payment_mode: data.prepaid,
      cod_amount: data.prepaid === 'cod' ? parseFloat(data.cod) : "",
    };
  
    try {
      const response = await axios.post(Base_Url + priceEstimatorPublic, requestBody, { headers });
  
      if (response.data.status === "success") {
        setData(response.data);
        reset();
        setIsModalOpen(true);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsProcessing(false);
    }
  };
  
    return (
        <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
            {/* Background particles */}
            <div className="absolute inset-0 opacity-10">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute rounded-full bg-amber-300"
                        style={{
                            width: `${Math.random() * 50 + 10}px`,
                            height: `${Math.random() * 50 + 10}px`,
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            animationDuration: `${Math.random() * 20 + 10}s`,
                            animationDelay: `${Math.random() * 5}s`,
                            animation: 'float 15s infinite ease-in-out'
                        }}
                    />
                ))}
            </div>

            <div className="w-full max-w-6xl z-10 bg-white rounded-2xl shadow-xl overflow-hidden border border-[#eedff0]">
                {/* Header section */}
                <div className="relative bg-sky-600 pt-8 pb-16">
                    <div className="absolute bottom-0 left-0 right-0">
                        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-10 text-white fill-current">
                            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"></path>
                        </svg>
                    </div>
                    <div className="text-center relative z-10">
                        <div className="inline-flex p-3 rounded-full bg-white shadow-lg mb-4">
                            <TruckIcon size={32} className="text-gray-600" />
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-600 mb-2">Shipping Rate Estimator</h1>
                        <p className="text-gray-600 text-lg max-w-md mx-auto">
                            Fast, accurate shipping quotes for your packages
                        </p>
                    </div>
                </div>

                {/* Form section */}
                <div className="bg-white p-6 md:p-8 -mt-6 rounded-t-3xl">
                    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                        {/* Origin and Destination */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="pickupPincode" className="block text-sm font-medium text-black">Origin Pincode*</label>
                                <input
                                    type="text"
                                    id="pickupPincode"
                                    maxLength="6"
                                    placeholder="Enter 6-digit pincode"
                                    className={`w-full px-4 py-2 border ${errors.pickupPincode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent`}
                                    {...register('pickupPincode')}
                                    onChange={(e) => handlePincodeChange(e, 'pickup')}
                                />
                                {errors.pickupPincode ? (
                                    <p className="text-red-500 text-sm">{errors.pickupPincode.message}</p>
                                ) : (
                                    pickupCity && <p className="text-sm text-purple-600 mt-1">Pickup City: {pickupCity}</p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label htmlFor="dropPincode" className="block text-sm font-medium text-black">Destination Pincode*</label>
                                <input
                                    type="text"
                                    id="dropPincode"
                                    maxLength="6"
                                    placeholder="Enter 6-digit pincode"
                                    className={`w-full px-4 py-2 border ${errors.dropPincode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent`}
                                    {...register('dropPincode')}
                                    onChange={(e) => handlePincodeChange(e, 'drop')}
                                />
                                {errors.dropPincode ? (
                                    <p className="text-red-500 text-sm">{errors.dropPincode.message}</p>
                                ) : (
                                    dropCity && <p className="text-sm text-purple-600 mt-1">Drop City: {dropCity}</p>
                                )}
                            </div>
                        </div>

                        {/* Weight and Dimensions */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="weight" className="block text-sm font-medium text-black">Weight*</label>
                                <div className="flex space-x-2">
                                    <input
                                        type="number"
                                        id="weight"
                                        step="0.01"
                                        placeholder="0.0"
                                        className={`w-full px-4 py-2 border ${errors.weight ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent`}
                                        {...register('weight')}
                                    />
                                    <select
                                        id="weightUnit"
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent"
                                        {...register('weightUnit')}
                                    >
                                        <option value="gram">B2C</option>
                                        {/* <option value="kg">Kilograms</option> */}
                                    </select>
                                </div>
                                {errors.weight && <p className="text-red-500 text-sm">{errors.weight.message}</p>}
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-black">Dimensions (cm)*</label>
                                <div className="grid grid-cols-3 gap-2">
                                    <input
                                        type="number"
                                        id="length"
                                        step="0.01"
                                        placeholder="Length"
                                        className={`w-full px-3 py-2 border ${errors.length ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent`}
                                        {...register('length')}
                                    />
                                    <input
                                        type="number"
                                        id="width"
                                        step="0.01"
                                        placeholder="Width"
                                        className={`w-full px-3 py-2 border ${errors.width ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent`}
                                        {...register('width')}
                                    />
                                    <input
                                        type="number"
                                        id="height"
                                        step="0.01"
                                        placeholder="Height"
                                        className={`w-full px-3 py-2 border ${errors.height ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent`}
                                        {...register('height')}
                                    />
                                </div>
                                {(errors.length || errors.width || errors.height) && (
                                    <p className="text-red-500 text-sm">All dimension fields are required</p>
                                )}
                            </div>
                        </div>


                        {/* Payment Type */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="prepaid" className="block text-sm font-medium text-black">Payment Method*</label>
                                <select
                                    id="prepaid"
                                    className={`w-full px-4 py-2 border ${errors.prepaid ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent`}
                                    {...register('prepaid')}
                                    onChange={handlePrepaidChange}
                                >
                                    <option value="">Select Payment Method</option>
                                    <option value="prepaid">Prepaid</option>
                                    <option value="cod">Cash on Delivery (COD)</option>
                                </select>
                                {errors.prepaid && <p className="text-red-500 text-sm">{errors.prepaid.message}</p>}
                            </div>

                            {prepaidValue === 'cod' && (
                                <div className="space-y-2">
                                    <label htmlFor="codAmount" className="block text-sm font-medium text-black">COD Amount*</label>
                                    <input
                                        type="number"
                                        id="codAmount"
                                        step="0.01"
                                        placeholder="Enter COD amount"
                                        className={`w-full px-4 py-2 border ${errors.codAmount ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-[#f2d0f5] focus:border-transparent`}
                                        {...register('codAmount')}
                                    />
                                    {errors.codAmount && <p className="text-red-500 text-sm">{errors.codAmount.message}</p>}
                                </div>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-center mt-8">
                            <button
                                type="submit"
                                disabled={isProcessing}
                                className="relative bg-gradient-to-r from-sky-400 to-sky-600 text-white font-medium px-12 py-4 rounded-lg hover:from-sky-500 hover:to-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-600 focus:ring-offset-2 transition-all shadow-lg disabled:opacity-70"
                            >
                                <span className="flex items-center">
                                    {isProcessing ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <TruckIcon size={20} className="mr-2" />
                                            Get Shipping Estimate
                                        </>
                                    )}
                                </span>
                            </button>
                        </div>
                    </form>

                    {/* Features */}
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                        {[
                            { title: 'Real-time Rates', desc: 'Instant quotes from multiple carriers' },
                            { title: 'Accurate Pricing', desc: 'No hidden fees or surprises' },
                            { title: 'Global Coverage', desc: 'Ship to 200+ countries worldwide' }
                        ].map((feature, i) => (
                            <div key={i} className="bg-sky-50 p-4 rounded-lg border border-sky-100">
                                <h3 className="font-bold text-gray-800">{feature.title}</h3>
                                <p className="text-sm text-gray-600">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                    <ShippingEstimateModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        estimateData={data}
                    />
                </div>
            </div>
        </div>
    );
};

export default ShippingRateCalculator;